import { useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Users, BarChart3, Gamepad2, ArrowRight, Star, Zap, ChevronDown, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

// Main 6 games with appropriate icons
const FEATURED_GAMES = [
  { name: "101 Okey", icon: "13", isText: true, tileColor: "#1E88E5", color: "#C6A87C", description: "Klasik 101 taşlı okey" },
  { name: "Batak", icon: "♠", color: "#F61225", description: "Koz kağıt oyunu" },
  { name: "Pişti", icon: "🂡", color: "#4DFFFF", description: "İskambil kart oyunu" },
  { name: "Tavla", icon: "🎲", color: "#8B4513", description: "Zar ve pul oyunu" },
  { name: "King", icon: "♛", color: "#9333EA", description: "Stratejik kart oyunu" },
  { name: "Dama", icon: "◉", color: "#1E3A5F", description: "Strateji taş oyunu" },
];

// All supported games
const ALL_GAMES = [
  { category: "Okey & Taş Oyunları", games: ["101 Okey", "Okey", "Çanak Okey", "Eşli Okey", "Rummikub", "Domino"] },
  { category: "Batak Türevleri", games: ["Batak", "Eşli Batak", "Gömmeli Batak", "İhalesiz Batak", "Spades"] },
  { category: "Tavla Türevleri", games: ["Tavla", "Eşli Tavla", "Mars Tavla", "Güneş Tavla", "Türk Tavlası"] },
  { category: "İskambil Oyunları", games: ["Pişti", "King", "Serbest King", "51", "Remi", "Briç", "Konken", "Blackjack", "Poker"] },
  { category: "Masa & Strateji", games: ["Dama", "Satranç", "UNO"] },
  { category: "Sosyal Oyunlar", games: ["Tombala", "Tabu"] },
];

export default function Landing() {
  const [showAllGames, setShowAllGames] = useState(false);
  
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1676746424139-77f8bd8922a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxzb2NjZXIlMjBzdGFkaXVtJTIwbmlnaHQlMjBsaWdodHN8ZW58MHx8fHwxNzY5NjUxMTM0fDA&ixlib=rb-4.1.0&q=85')` 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050E2E]/70 to-[#050E2E]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24">
          {/* Nav */}
          <nav className="flex items-center justify-between mb-16">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_game-tracker-92/artifacts/62gbiyum_WhatsApp%20Image%202026-01-30%20at%2002.16.12.jpeg" 
                alt="Kahve LİGİ Logo"
                className="w-24 h-24 rounded-2xl object-contain"
              />
              <span className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider gold-gradient-text">
                Kahve LİGİ
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-white hover:bg-white/10">
                  Giriş Yap
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 font-['Barlow_Condensed'] uppercase tracking-wider font-bold">
                  Kayıt Ol
                </Button>
              </Link>
            </div>
          </nav>

          {/* Hero Content */}
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold font-['Barlow_Condensed'] uppercase tracking-wide text-white mb-6">
              Kahve Kültürünü
              <br />
              <span className="gold-gradient-text">Dijitalleştir</span>
            </h1>
            <p className="text-xl text-[#B1C1C1] max-w-2xl mx-auto mb-8">
              Okey, Batak, Tavla ve daha fazlası. Arkadaşlarınla lig oluştur, 
              skorlarını kaydet, AI yorumlarıyla eğlen.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={handleGoogleLogin}
                className="bg-white text-[#050E2E] hover:bg-white/90 font-['Barlow_Condensed'] uppercase tracking-wider font-bold px-8 py-6 text-lg"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google ile Başla
              </Button>
              <Link to="/register">
                <Button 
                  variant="outline" 
                  className="border-[#C6A87C] text-[#C6A87C] hover:bg-[#C6A87C]/10 font-['Barlow_Condensed'] uppercase tracking-wider font-bold px-8 py-6 text-lg"
                >
                  Email ile Kayıt
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Games Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white mb-4">
            Desteklenen Oyunlar
          </h2>
          <p className="text-[#B1C1C1]">Türkiye kahve kültürünün tüm klasikleri</p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {FEATURED_GAMES.map((game, index) => (
            <div 
              key={game.name}
              className="glass-card p-6 text-center hover:-translate-y-2 transition-transform cursor-pointer group"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center transition-transform group-hover:scale-110">
                {game.isText ? (
                  // Okey taşı stili
                  <div 
                    className="w-10 h-14 rounded-lg flex items-center justify-center shadow-lg"
                    style={{ 
                      backgroundColor: '#FFF8E7',
                      border: '3px solid #D4A574',
                    }}
                  >
                    <span 
                      className="text-xl font-black"
                      style={{ color: game.tileColor || '#1E88E5' }}
                    >
                      {game.icon}
                    </span>
                  </div>
                ) : (
                  <span 
                    className="text-5xl"
                    style={{ color: game.color }}
                  >
                    {game.icon}
                  </span>
                )}
              </div>
              <p className="font-['Barlow_Condensed'] uppercase tracking-wider text-white font-bold">
                {game.name}
              </p>
            </div>
          ))}
        </div>
        
        {/* More Games Button */}
        <div className="text-center mt-8">
          <Button
            variant="outline"
            className="border-[#C6A87C]/50 text-[#C6A87C] hover:bg-[#C6A87C]/10 gap-2"
            onClick={() => setShowAllGames(true)}
          >
            <Gamepad2 className="w-4 h-4" />
            +26 Oyun Daha
            <ChevronDown className="w-4 h-4" />
          </Button>
        </div>
        
        {/* All Games Dialog */}
        <Dialog open={showAllGames} onOpenChange={setShowAllGames}>
          <DialogContent className="bg-[#0A1635] border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#C6A87C]">
                Tüm Desteklenen Oyunlar
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 mt-4">
              {ALL_GAMES.map((category) => (
                <div key={category.category}>
                  <h3 className="text-sm font-bold text-[#4DFFFF] uppercase tracking-wider mb-3">
                    {category.category}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {category.games.map((game) => (
                      <span
                        key={game}
                        className="px-3 py-1.5 rounded-full bg-white/5 text-white text-sm border border-white/10"
                      >
                        {game}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-white/10 text-center">
              <p className="text-[#64748B] text-sm mb-3">Aradığın oyun yok mu?</p>
              <Link to="/register">
                <Button className="bg-[#C6A87C] hover:bg-[#B8956E] text-[#050E2E]">
                  Üye Ol ve Kendi Oyununu Ekle
                </Button>
              </Link>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="glass-card p-8">
            <div className="w-14 h-14 rounded-xl bg-[#4DFFFF]/20 flex items-center justify-center mb-6">
              <Users className="w-7 h-7 text-[#4DFFFF]" />
            </div>
            <h3 className="text-xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white mb-3">
              Sosyal Platform
            </h3>
            <p className="text-[#B1C1C1]">
              Arkadaşlarını takip et, mesajlaş, aktivite akışında neler olup bittiğini gör.
            </p>
          </div>
          
          <div className="glass-card p-8">
            <div className="w-14 h-14 rounded-xl bg-[#C6A87C]/20 flex items-center justify-center mb-6">
              <Trophy className="w-7 h-7 text-[#C6A87C]" />
            </div>
            <h3 className="text-xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white mb-3">
              Lig & Turnuva
            </h3>
            <p className="text-[#B1C1C1]">
              Kendi ligini kur, haftalık skorları kaydet, puan tablosunu takip et.
            </p>
          </div>
          
          <div className="glass-card p-8">
            <div className="w-14 h-14 rounded-xl bg-[#F308C9]/20 flex items-center justify-center mb-6">
              <Zap className="w-7 h-7 text-[#F308C9]" />
            </div>
            <h3 className="text-xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white mb-3">
              AI Yorumlar
            </h3>
            <p className="text-[#B1C1C1]">
              Her hafta sonunda yapay zeka destekli spor yorumcusu tarzında analizler.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="glass-card p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#4DFFFF]/10 to-[#C6A87C]/10" />
          <div className="relative">
            <h2 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white mb-4">
              Hazır mısın?
            </h2>
            <p className="text-[#B1C1C1] mb-8 max-w-xl mx-auto">
              Şimdi katıl, arkadaşlarını davet et ve kahve masası rekabetini dijitale taşı.
            </p>
            <Button 
              onClick={handleGoogleLogin}
              className="bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 font-['Barlow_Condensed'] uppercase tracking-wider font-bold px-8 py-6 text-lg"
            >
              Hemen Başla
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Trophy className="w-5 h-5 text-[#C6A87C]" />
              <span className="text-sm text-[#64748B]">Kahve LİGİ © 2025</span>
            </div>
            <p className="text-sm text-[#64748B]">Kahve kültürünü dijitalleştiriyoruz</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
